import { LoadState } from "../../../constants/enums";
import { getUniqueId } from "../../../helpers";
import { IIftaQuarterlyRow } from "./iftaImport.types";

export interface IRiskProfileRadiusOfOperationRow {
  key: string;
  vehicles: string | null;
  radius: string | null;
  cities_visited: string | null;
  province: string | null;
  attached_equipment: string | null;
  value_of_attachment: string | null;
  km_0_40_pct: number | null;
  km_41_80_pct: number | null;
  km_81_160_pct: number | null;
  km_161_400_pct: number | null;
  km_401_800_pct: number | null;
  km_801_1600_pct: number | null;
  km_1601_2400_pct: number | null;
  km_2401_plus_pct: number | null;
}

export interface IRiskProfileFleetHistoryRow {
  key: string;
  term: string | null;
  insurer: string | null;
  policy_number: string | null;
  policy_type: string | null;
  number_of_vehicles: number | null;
  number_of_trailers: number | null;
  claims_payout: number | null;
  term_premium: number | null;
  current_status: string | null;
}

export interface IRiskProfileYardAddressSecurityRow {
  key: string;
  incremental_number: number;
  address: string | null;
  security: string[];
}

export interface IRiskProfileCargoCglCoverageRow {
  key: string;
  coverage: string;
  limit: number | null;
  deductible: number | null;
}

export interface IRiskProfileVehicleCoverageScheduleRow {
  key: string;
  incremental_number: number;
  customer_policy_id: number | null;
}

export interface IRiskProfileDriverHiring {
  documented_hiring_policy: "Y" | "N" | "Not Applicable" | null;
  minimum_hiring_requirements: string | null;
  resumes_applications_retained: "Y" | "N" | "Not Applicable" | null;
  references_checked: "Y" | "N" | "Not Applicable" | null;
  criminal_background_checks: "Y" | "N" | "Not Applicable" | null;
  employment_references_documented: "Y" | "N" | "Not Applicable" | null;
  commercial_experience_verified: "Y" | "N" | "Not Applicable" | null;
  license_verified: "Y" | "N" | "Not Applicable" | null;
  abstracts_review_frequency: string | null;
  cvor_records_reviewed: "Y" | "N" | "Not Applicable" | null;
  medical_certificates_current: "Y" | "N" | "Not Applicable" | null;
  air_brake_endorsements_verified: "Y" | "N" | "Not Applicable" | null;
  legal_work_status_maintained: "Y" | "N" | "Not Applicable" | null;
  qualification_files_complete: "Y" | "N" | "Not Applicable" | null;
  min_age_hired: number | null;
  min_experience_requirement: string | null;
  formal_orientation_program: "Y" | "N" | "Not Applicable" | null;
  orientation_includes: string[];
  sign_orientation_documents: "Y" | "N" | "Not Applicable" | null;
}

export interface IRiskProfileDriverTraining {
  ongoing_training_conducted: "Y" | "N" | "Not Applicable" | null;
  training_frequency: string | null;
  training_documented: "Y" | "N" | "Not Applicable" | null;
  trained_on: string[];
  new_regulations_communicated: "Y" | "N" | "Not Applicable" | null;
}

export interface IRiskProfileDriverMonitoring {
  abstracts_monitored: "Y" | "N" | "Not Applicable" | null;
  monitoring_frequency: string | null;
  telematics_used: "Y" | "N" | "Not Applicable" | null;
  speeding_monitored: "Y" | "N" | "Not Applicable" | null;
  harsh_braking_monitored: "Y" | "N" | "Not Applicable" | null;
  seat_belt_monitored: "Y" | "N" | "Not Applicable" | null;
  idling_reports_reviewed: "Y" | "N" | "Not Applicable" | null;
  gps_reports_reviewed: "Y" | "N" | "Not Applicable" | null;
  customer_complaints_tracked: "Y" | "N" | "Not Applicable" | null;
}

export interface IRiskProfileDriverDiscipline {
  progressive_discipline_policy: "Y" | "N" | "Not Applicable" | null;
  violations_triggering_discipline: string | null;
  discipline_documented: "Y" | "N" | "Not Applicable" | null;
  coaching_sessions_documented: "Y" | "N" | "Not Applicable" | null;
  repeat_offenders_monitored: "Y" | "N" | "Not Applicable" | null;
  drivers_rewarded_safe_driving: "Y" | "N" | "Not Applicable" | null;
  violations_monitored: string[];
}

export interface IRiskProfileVehicleManagement {
  fleet_list_maintained: "Y" | "N" | "Not Applicable" | null;
  vehicle_assignments_documented: "Y" | "N" | "Not Applicable" | null;
  vin_records_maintained: "Y" | "N" | "Not Applicable" | null;
  registration_current: "Y" | "N" | "Not Applicable" | null;
  insurance_current: "Y" | "N" | "Not Applicable" | null;
  dashcams_equipped: "Y" | "N" | "Not Applicable" | null;
  telematics_installed: "Y" | "N" | "Not Applicable" | null;
  preventive_maintenance_schedule: "Y" | "N" | "Not Applicable" | null;
  maintenance_software: "Y" | "N" | "Not Applicable" | null;
  service_intervals: string | null;
  maintenance_records_retained: "Y" | "N" | "Not Applicable" | null;
  repairs_documented: "Y" | "N" | "Not Applicable" | null;
  daily_inspection_reports_completed: "Y" | "N" | "Not Applicable" | null;
  deficiencies_repaired: "Y" | "N" | "Not Applicable" | null;
  defect_reporting_process: "Y" | "N" | "Not Applicable" | null;
  inspection_records_retained: "Y" | "N" | "Not Applicable" | null;
  vehicle_condition_check: string[];
}

export interface IRiskProfileCargoSafety {
  cargo_securement_policy: "Y" | "N" | "Not Applicable" | null;
  driver_training: "Y" | "N" | "Not Applicable" | null;
  load_inspections: "Y" | "N" | "Not Applicable" | null;
  weight_compliance: "Y" | "N" | "Not Applicable" | null;
  axle_weights_verified: "Y" | "N" | "Not Applicable" | null;
  load_distribution: "Y" | "N" | "Not Applicable" | null;
  hazardous_goods_procedures: "Y" | "N" | "Not Applicable" | null;
  securement_equipment_inspected: "Y" | "N" | "Not Applicable" | null;
  damaged_straps_removed: "Y" | "N" | "Not Applicable" | null;
  chains_inspected: "Y" | "N" | "Not Applicable" | null;
  tarps_inspected: "Y" | "N" | "Not Applicable" | null;
}

// Security Measures (Section 02 / Operations & Risk Details)
export interface IRiskProfileSecurityMeasures {
  vehicles_parked_in_secured_yard: "Y" | "N" | "Not Applicable" | null;
  lot_locked_at_night: "Y" | "N" | "Not Applicable" | null;
  power_units_anti_theft: "Y" | "N" | "Not Applicable" | null;
  loaded_trailers_secured_when_parked: "Y" | "N" | "Not Applicable" | null;
  where_vehicles_parked_off_duty: string | null;
  number_vehicles_in_secured_yard: number | null;
  total_value_including_cargo: number | null;
  lot_has: string[];
}

// Non-Owned Trailers (Section 05)
export interface IRiskProfileNonOwnedTrailers {
  use_non_owned_trailers: "Y" | "N" | "Not Applicable" | null;
  trailer_type: string[];
  number_average: number | null;
  number_maximum: number | null;
  value_average: number | null;
  value_maximum: number | null;
}

// Commodities Transported (Section 05)
export interface IRiskProfileCommodityRow {
  key: string;
  commodity: string | null;
  pct_of_loads: number | null;
  max_value_per_load: number | null;
  annual_revenue_pct: number | null;
}

// Territory Covered (Section 05)
export interface IRiskProfileTerritory {
  total_annual_mileage_km: number | null;
  canada_pct: number | null;
  usa_pct: number | null;
  usa_travel_over_100km_from_border: "Y" | "N" | "Not Applicable" | null;
  more_than_5_trips_per_month_usa: "Y" | "N" | "Not Applicable" | null;
  telematics_installed: "Y" | "N" | "Not Applicable" | null;
  telematics_count: number | null;
  fuel_tax_ifta_attached: "Y" | "N" | "Not Applicable" | null;
  // --- IFTA extensions (optional) ---
  canada_km?: number | null;
  usa_km?: number | null;
  number_of_usa_states?: number | null;
  number_of_canada_provinces?: number | null;
  quarterly_breakdown?: IIftaQuarterlyRow[] | null;
  ifta_import?: {
    source: "IFTA_IMPORT";
    account_number: string;
    period_start: string;
    period_end: string;
    imported_at: string; // ISO timestamp, for the badge
  } | null;
}

// Applicant's Operations (Section 01)
export interface IRiskProfileApplicantOperations {
  haul_owned_product_exclusively: "Y" | "N" | "Not Applicable" | null;
  haul_non_owned_goods_exclusive_agreement: "Y" | "N" | "Not Applicable" | null;
  owners_operated_under_another_name: "Y" | "N" | "Not Applicable" | null;
  back_hauls_guaranteed: "Y" | "N" | "Not Applicable" | null;
  pct_owned_products: number | null;
  pct_non_owned_goods: number | null;
  years_operated_under_present_name: number | null;
  revenue_long_haul_trucking: number | null;
  companies_under_exclusive_agreement: string | null;
  previous_business_name: string | null;
  previous_cvor_rin_dot_mc: string | null;
  carrier_type: string[];
}

// Cargo Operations (Section 07G)
export interface IRiskProfileCargoOperations {
  haul_loads_require_escort: "Y" | "N" | "Not Applicable" | null;
  haul_oversize_overweight: "Y" | "N" | "Not Applicable" | null;
  cargo_value_exceeds_500k: "Y" | "N" | "Not Applicable" | null;
  placards_required: "Y" | "N" | "Not Applicable" | null;
  passengers_allowed: "Y" | "N" | "Not Applicable" | null;
  operate_under_uiia: "Y" | "N" | "Not Applicable" | null;
  vehicles_used_off_road_logging: "Y" | "N" | "Not Applicable" | null;
  residential_home_delivery: "Y" | "N" | "Not Applicable" | null;
  loads_brokered_to_others: "Y" | "N" | "Not Applicable" | null;
  shipper_contracts_differ_from_bol: "Y" | "N" | "Not Applicable" | null;
  brokered_revenue: number | null;
  bill_of_lading_type: string | null;
  declared_value_percentage: number | null;
  bill_of_lading_issued_by: string | null;
}

// Premises & Required Documents (Section 08)
export interface IRiskProfilePremises {
  building_construction_walls: string | null;
  roof: string | null;
  floor: string | null;
  area_of_building: string | null;
  heating: string | null;
  electricity: string | null;
  occupancies: string | null;
  max_vehicles_stored_inside_total_value: string | null;
  documents_required: string[];
}

export interface IRiskProfileDriver {
  driver_number: string | null;
  driver_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  license_number: string | null;
  province: string | null;
  date_of_current_licence: string | null;
  class_licensed: string | null;
  driver_training_cert: string | null;
  employed_since: string | null;
  yrs_exp_similar_auto: string | null;
}

export interface IRiskProfileVehicle {
  incremental_number: number | null;
  vehicle_number: number | null;
  coverage_number: number | null;
  coverage_name: string | null;
  vin: string | null;
  status: string;
  total_premium: number | null;
  minimum_retained_premium: number | null;
  year: string | null;
  make: string | null;
  model: string | null;
  body_type: string | null;
  gross_vehicle_weight: string | null;
  owned_leased: string | null;
  plate_number: string | null;
  usa_exposure: string | null;
  usa_mo_radius: string | null;
  km_0_40_pct: number | null;
  km_41_80_pct: number | null;
  km_81_160_pct: number | null;
  km_161_400_pct: number | null;
  km_401_800_pct: number | null;
  km_801_1600_pct: number | null;
  km_1601_2400_pct: number | null;
  km_2401_plus_pct: number | null;
  est_annual_distance: string | null;
}

export interface IRiskProfile {
  incremental_number: number | null;
  customer_policy_id: number | null;
  status: "ACTIVE" | "INACTIVE";
  created_at: string | null;

  // Drivers tab
  drivers: IRiskProfileDriver[];

  // Vehicles tab
  vehicles: IRiskProfileVehicle[];

  // General tab
  rin_number: string | null;
  cvor_number: string | null;
  mc_number: string | null;
  dot_number: string | null;
  cvor_status: string | null;
  cvor_violation_rate: number | null;
  vehicle_out_of_service_pct: number | null;
  driver_out_of_service_pct: number | null;
  brief_history_of_risk: string | null;
  description_of_operations: string | null;
  drivers_take_vehicle_home: "Y" | "N" | "Not Applicable" | null;
  loaded_trailers_unattended: "Y" | "N" | "Not Applicable" | null;
  remarks: string | null;
  radius_of_operation: IRiskProfileRadiusOfOperationRow[];
  fleet_history: IRiskProfileFleetHistoryRow[];
  yard_address_security: IRiskProfileYardAddressSecurityRow[];

  // Security Measures
  security_measures: IRiskProfileSecurityMeasures;

  // Applicant Operations
  applicant_operations: IRiskProfileApplicantOperations;

  // Non-Owned Trailers
  non_owned_trailers: IRiskProfileNonOwnedTrailers;

  // Commodities Transported
  commodities_transported: IRiskProfileCommodityRow[];

  // Territory Covered
  territory: IRiskProfileTerritory;

  // Coverages tab
  vehicle_coverage_schedule: IRiskProfileVehicleCoverageScheduleRow[];
  cargo_cgl_coverage: IRiskProfileCargoCglCoverageRow[];

  // Questionnaire tabs
  driver_hiring: IRiskProfileDriverHiring;
  driver_training: IRiskProfileDriverTraining;
  driver_monitoring: IRiskProfileDriverMonitoring;
  driver_discipline: IRiskProfileDriverDiscipline;
  vehicle_management: IRiskProfileVehicleManagement;
  cargo_safety: IRiskProfileCargoSafety;
  cargo_operations: IRiskProfileCargoOperations;
  premises: IRiskProfilePremises;
}

export interface IRiskProfileState {
  list: {
    data: IRiskProfile[];
    loading: LoadState;
    error: string | null;
  };
  form: {
    data: IRiskProfile;
    loading: LoadState;
    saveLoading: boolean;
    error: string | null;
  };
}


// Default Cargo & CGL coverage rows seeded on creation (Requirement 8.2)
export const CARGO_CGL_DEFAULT_ROWS: IRiskProfileCargoCglCoverageRow[] = [
  { key: getUniqueId(), coverage: "Motor Truck Cargo (per vehicle)", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Cargo Catastrophic Limit", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Terminal Legal Liability", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Commercial General Liability - Bodily Injury & Property Damage", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Standard Non-Owned Automobile Policy - SPF No. 6", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Legal Liability For Damage To Hired Automobiles Endorsement - SEF No. 94", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Tenants Legal Liability", limit: null, deductible: null },
  { key: getUniqueId(), coverage: "Warehouseman's Legal Liability", limit: null, deductible: null },
];

export const INITIAL_RISK_PROFILE: IRiskProfile = {
  incremental_number: null,
  customer_policy_id: null,
  status: "ACTIVE",
  created_at: null,

  // Drivers tab
  drivers: [],

  // Vehicles tab
  vehicles: [],

  // General tab
  rin_number: null,
  cvor_number: null,
  mc_number: null,
  dot_number: null,
  cvor_status: null,
  cvor_violation_rate: null,
  vehicle_out_of_service_pct: null,
  driver_out_of_service_pct: null,
  brief_history_of_risk: null,
  description_of_operations: null,
  drivers_take_vehicle_home: null,
  loaded_trailers_unattended: null,
  remarks: null,
  radius_of_operation: [
    {
      key: getUniqueId(),
      vehicles: null,
      radius: null,
      cities_visited: null,
      province: null,
      attached_equipment: null,
      value_of_attachment: null,
      km_0_40_pct: null,
      km_41_80_pct: null,
      km_81_160_pct: null,
      km_161_400_pct: null,
      km_401_800_pct: null,
      km_801_1600_pct: null,
      km_1601_2400_pct: null,
      km_2401_plus_pct: null,
    },
  ],
  fleet_history: [
    {
      key: getUniqueId(),
      term: null,
      insurer: null,
      policy_number: null,
      policy_type: null,
      number_of_vehicles: null,
      number_of_trailers: null,
      claims_payout: null,
      term_premium: null,
      current_status: null,
    },
  ],
  yard_address_security: [
    {
      key: getUniqueId(),
      incremental_number: 1,
      address: null,
      security: [],
    },
  ],

  // Security Measures
  security_measures: {
    vehicles_parked_in_secured_yard: null,
    lot_locked_at_night: null,
    power_units_anti_theft: null,
    loaded_trailers_secured_when_parked: null,
    where_vehicles_parked_off_duty: null,
    number_vehicles_in_secured_yard: null,
    total_value_including_cargo: null,
    lot_has: [],
  },

  // Applicant Operations
  applicant_operations: {
    haul_owned_product_exclusively: null,
    haul_non_owned_goods_exclusive_agreement: null,
    owners_operated_under_another_name: null,
    back_hauls_guaranteed: null,
    pct_owned_products: null,
    pct_non_owned_goods: null,
    years_operated_under_present_name: null,
    revenue_long_haul_trucking: null,
    companies_under_exclusive_agreement: null,
    previous_business_name: null,
    previous_cvor_rin_dot_mc: null,
    carrier_type: [],
  },

  // Non-Owned Trailers
  non_owned_trailers: {
    use_non_owned_trailers: null,
    trailer_type: [],
    number_average: null,
    number_maximum: null,
    value_average: null,
    value_maximum: null,
  },

  // Commodities Transported
  commodities_transported: [
    {
      key: getUniqueId(),
      commodity: null,
      pct_of_loads: null,
      max_value_per_load: null,
      annual_revenue_pct: null,
    },
  ],

  // Territory Covered
  territory: {
    total_annual_mileage_km: null,
    canada_pct: null,
    usa_pct: null,
    usa_travel_over_100km_from_border: null,
    more_than_5_trips_per_month_usa: null,
    telematics_installed: null,
    telematics_count: null,
    fuel_tax_ifta_attached: null,
  },

  // Coverages tab
  vehicle_coverage_schedule: [
    {
      key: getUniqueId(),
      incremental_number: 1,
      customer_policy_id: null,
    },
  ],
  cargo_cgl_coverage: CARGO_CGL_DEFAULT_ROWS,

  // Questionnaire tabs
  driver_hiring: {
    documented_hiring_policy: null,
    minimum_hiring_requirements: null,
    resumes_applications_retained: null,
    references_checked: null,
    criminal_background_checks: null,
    employment_references_documented: null,
    commercial_experience_verified: null,
    license_verified: null,
    abstracts_review_frequency: null,
    cvor_records_reviewed: null,
    medical_certificates_current: null,
    air_brake_endorsements_verified: null,
    legal_work_status_maintained: null,
    qualification_files_complete: null,
    min_age_hired: null,
    min_experience_requirement: null,
    formal_orientation_program: null,
    orientation_includes: [],
    sign_orientation_documents: null,
  },
  driver_training: {
    ongoing_training_conducted: null,
    training_frequency: null,
    training_documented: null,
    trained_on: [],
    new_regulations_communicated: null,
  },
  driver_monitoring: {
    abstracts_monitored: null,
    monitoring_frequency: null,
    telematics_used: null,
    speeding_monitored: null,
    harsh_braking_monitored: null,
    seat_belt_monitored: null,
    idling_reports_reviewed: null,
    gps_reports_reviewed: null,
    customer_complaints_tracked: null,
  },
  driver_discipline: {
    progressive_discipline_policy: null,
    violations_triggering_discipline: null,
    discipline_documented: null,
    coaching_sessions_documented: null,
    repeat_offenders_monitored: null,
    drivers_rewarded_safe_driving: null,
    violations_monitored: [],
  },
  vehicle_management: {
    fleet_list_maintained: null,
    vehicle_assignments_documented: null,
    vin_records_maintained: null,
    registration_current: null,
    insurance_current: null,
    dashcams_equipped: null,
    telematics_installed: null,
    preventive_maintenance_schedule: null,
    maintenance_software: null,
    service_intervals: null,
    maintenance_records_retained: null,
    repairs_documented: null,
    daily_inspection_reports_completed: null,
    deficiencies_repaired: null,
    defect_reporting_process: null,
    inspection_records_retained: null,
    vehicle_condition_check: [],
  },
  cargo_safety: {
    cargo_securement_policy: null,
    driver_training: null,
    load_inspections: null,
    weight_compliance: null,
    axle_weights_verified: null,
    load_distribution: null,
    hazardous_goods_procedures: null,
    securement_equipment_inspected: null,
    damaged_straps_removed: null,
    chains_inspected: null,
    tarps_inspected: null,
  },
  cargo_operations: {
    haul_loads_require_escort: null,
    haul_oversize_overweight: null,
    cargo_value_exceeds_500k: null,
    placards_required: null,
    passengers_allowed: null,
    operate_under_uiia: null,
    vehicles_used_off_road_logging: null,
    residential_home_delivery: null,
    loads_brokered_to_others: null,
    shipper_contracts_differ_from_bol: null,
    brokered_revenue: null,
    bill_of_lading_type: null,
    declared_value_percentage: null,
    bill_of_lading_issued_by: null,
  },
  premises: {
    building_construction_walls: null,
    roof: null,
    floor: null,
    area_of_building: null,
    heating: null,
    electricity: null,
    occupancies: null,
    max_vehicles_stored_inside_total_value: null,
    documents_required: [],
  },
};

export const initialRiskProfileState: IRiskProfileState = {
  list: {
    data: [],
    loading: LoadState.NotLoaded,
    error: null,
  },
  form: {
    data: INITIAL_RISK_PROFILE,
    loading: LoadState.NotLoaded,
    saveLoading: false,
    error: null,
  },
};
